/**
 * Ordered rule application (docs/event-model.md §5). Pure: rules see a copy
 * of the log whose severities reflect the escalations made so far.
 */
import { type AuditEvent, maxSeverity, severityRank } from "../events";
import type { AnalysisInput, Escalation, Finding, Rule } from "./types";

/** The log with `escalations` applied in order; returns `events` itself if none. */
export function applyEscalations<E extends AuditEvent>(
  events: readonly E[],
  escalations: readonly Escalation[],
): readonly E[] {
  if (escalations.length === 0) return events;
  const raised = new Map<number, E["severity"]>();
  for (const { seq, severity } of escalations) {
    raised.set(seq, maxSeverity(raised.get(seq) ?? severity, severity));
  }
  return events.map((e) => {
    const severity = raised.get(e.seq);
    return severity === undefined || severityRank(severity) <= severityRank(e.severity)
      ? e
      : { ...e, severity };
  });
}

export interface RuleResults {
  /** In rule order, not yet grouped or sorted (see finalizeFindings). */
  findings: Finding[];
  /** Only the ones that raised a severity, in the order they took effect. */
  escalations: Escalation[];
  /** The log with every escalation applied. */
  events: readonly AuditEvent[];
}

/** Runs `rules` in order over `input`. */
export function runRules(rules: readonly Rule[], input: AnalysisInput): RuleResults {
  const findings: Finding[] = [];
  const escalations: Escalation[] = [];
  let events = input.events;
  for (const rule of rules) {
    const output = rule.apply({ ...input, events });
    for (const { explanation, evidence, ...finding } of output.findings ?? []) {
      findings.push({
        rule: rule.id,
        ...finding,
        explanation: explanation ?? rule.explanation,
        evidence: [...new Set(evidence)].sort((a, b) => a - b),
      });
    }
    const current = new Map(events.map((e) => [e.seq, e.severity]));
    const applied: Escalation[] = [];
    for (const { seq, severity } of output.escalations ?? []) {
      const from = current.get(seq);
      if (from === undefined || severityRank(severity) <= severityRank(from)) continue;
      applied.push({ seq, severity, from, rule: rule.id });
      current.set(seq, severity);
    }
    escalations.push(...applied);
    events = applyEscalations(events, applied);
  }
  return { findings, escalations, events };
}
